import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientConfig } from '../config/clientConfig';
import { LOGIN_SUCCESS_EVENT } from '../authEvents';
import { api, extractErrorMessage } from '../api/client';
import { endpoints } from '../api/endpoints';
import { completeLogin } from '../utils/authSession';
import OtpVerification from './OtpVerification';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { formControlClass, formLabelClass } from './ui/fieldStyles';

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [sessionMessage, setSessionMessage] = useState(() => {
    try {
      return sessionStorage.getItem('sessionExpiredMessage') || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    if (sessionMessage) {
      try {
        sessionStorage.removeItem('sessionExpiredMessage');
      } catch {
        // ignore
      }
    }
  }, [sessionMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await api.post(endpoints.auth.login, { username, password });
      if (data?.otpRequired) {
        setUserEmail(data.email);
        setShowOtpScreen(true);
      } else if (data?.token) {
        completeLogin(data);
        try {
          localStorage.setItem('activeMenu', 'Dashboard');
        } catch {
          // ignore
        }
        try {
          window.dispatchEvent(new Event(LOGIN_SUCCESS_EVENT));
        } catch {
          // ignore
        }
        navigate('/', { replace: true });
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Invalid email/mobile or password'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSuccess = () => {
    try {
      localStorage.setItem('activeMenu', 'Dashboard');
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(new Event(LOGIN_SUCCESS_EVENT));
    } catch {
      // ignore
    }
    navigate('/', { replace: true });
  };

  const handleBackToLogin = () => {
    setShowOtpScreen(false);
    setPassword('');
    setError('');
  };

  if (showOtpScreen) {
    return (
      <OtpVerification
        email={userEmail}
        onSuccess={handleOtpSuccess}
        onBack={handleBackToLogin}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        {/* Logo and Company Name */}
        <div className="text-center mb-8">
          {clientConfig.loginLogoUrl ? (
            <img 
              src={clientConfig.loginLogoUrl} 
              alt="Logo" 
              className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg ring-4 ring-white/80"
            />
          ) : (
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600 flex items-center justify-center text-white text-3xl font-bold shadow-xl shadow-primary-900/20 ring-4 ring-white/60">
              {clientConfig.loginCompanyName.charAt(0)}
            </div>
          )}
          <h1 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">
            {clientConfig.loginCompanyName}
          </h1>
          <p className="text-slate-600">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-large border border-slate-200/80 p-8 sm:p-9 ring-1 ring-slate-900/5">
          <form onSubmit={handleSubmit} className="space-y-6">
            {sessionMessage && !error && (
              <div className="bg-sky-50/90 border border-sky-200/80 text-sky-900 px-4 py-3 rounded-xl text-sm">
                {sessionMessage}
              </div>
            )}
            {error && (
              <div className="bg-rose-50/90 border border-rose-200/80 text-rose-900 px-4 py-3 rounded-xl text-sm flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <div>
              <Input
                label="Email or Mobile"
                type="text"
                placeholder="Enter your email or mobile"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                error={error && !username ? 'Email or mobile is required' : ''}
              />
            </div>

            <div>
              <label className={formLabelClass}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`${formControlClass()} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-primary-600 transition-colors rounded-lg p-0.5"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded-md text-primary-600 border-slate-300 focus:ring-primary-500/30"
                />
                <span className="ml-2 text-sm text-slate-700">Remember me</span>
              </label>
              <a href="#" className="text-sm text-primary-700 hover:text-primary-800 font-semibold shrink-0">
                Forgot password?
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={loading}
              disabled={loading}
            >
              Sign In
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-slate-600">
          Developed by <span className="font-semibold text-slate-900">{clientConfig.developerName}</span>
        </div>
      </div>
    </div>
  );
}

export default Login;
